import { useUI } from '@/context/ui-context';
import { EnrichedEntity } from '@/lib/types';
import { useState } from 'react';

/** Lightweight marker used to trigger the diff dialog for a specific version. */
export interface VersionMarker {
  id: string;
  seq: number;
  dayKey: string;
}

interface UseEditorUIProps {
  initialOutlineOpen?: boolean;
  initialScenePropertiesOpen?: boolean;
}

export function useEditorUI(props: UseEditorUIProps = {}) {
  const { initialOutlineOpen = false, initialScenePropertiesOpen = true } = props;
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [isEntitiesDrawerOpen, setIsEntitiesDrawerOpen] = useState(false);
  const [isScenePropertiesOpen, setIsScenePropertiesOpen] = useState(initialScenePropertiesOpen);
  const [isOutlineDrawerOpen, setIsOutlineDrawerOpen] = useState(initialOutlineOpen);
  const [isWritersRoomOpen, setIsWritersRoomOpen] = useState(false);
  const { isFocusMode, setIsFocusMode } = useUI();
  const [drawerSearch, setDrawerSearch] = useState('');
  const [selectedEntitySummary, setSelectedEntitySummary] = useState<EnrichedEntity | null>(null);
  const [isEntityDrawerOpen, setIsEntityDrawerOpen] = useState(false);
  const [versionToCompare, setVersionToCompare] = useState<VersionMarker | null>(null);
  const [reconstructedVersionText, setReconstructedVersionText] = useState<string>('');
  const [reconstructedPreviousText, setReconstructedPreviousText] = useState<string>('');

  const handleToggleHistoryDrawer = () => {
    if (!isHistoryDrawerOpen) {
      closeSidebars()
    }
    setIsHistoryDrawerOpen(!isHistoryDrawerOpen);
  };

  const handleToggleEntitiesDrawer = () => {
    if (!isEntitiesDrawerOpen) {
      closeSidebars()
    }
    setIsEntitiesDrawerOpen(!isEntitiesDrawerOpen);
  };

  const handleToggleSceneProperties = () => {
    if (!isScenePropertiesOpen) {
      closeSidebars()
    }
    setIsScenePropertiesOpen(!isScenePropertiesOpen);
  };

  const handleToggleOutlineDrawer = () => {
    if (!isOutlineDrawerOpen) {
      closeSidebars()
    }
    setIsOutlineDrawerOpen(!isOutlineDrawerOpen);
  };

  const handleToggleWritersRoom = () => {
    if (!isWritersRoomOpen) {
      closeSidebars()
    }
    setIsWritersRoomOpen(!isWritersRoomOpen);
  };

  const handleToggleFocusMode = () => {
    const newVal = !isFocusMode;
    if (newVal) {
      closeSidebars();
    }
    setIsFocusMode(newVal);
  };

  const closeSidebars = () => {
    setIsHistoryDrawerOpen(false);
    setIsEntitiesDrawerOpen(false);
    setIsScenePropertiesOpen(false);
    setIsOutlineDrawerOpen(false);
    setIsWritersRoomOpen(false);
  };

  return {
    isHistoryDrawerOpen,
    setIsHistoryDrawerOpen,
    isEntitiesDrawerOpen,
    setIsEntitiesDrawerOpen,
    isScenePropertiesOpen,
    setIsScenePropertiesOpen,
    isOutlineDrawerOpen,
    setIsOutlineDrawerOpen,
    isWritersRoomOpen,
    setIsWritersRoomOpen,
    isFocusMode,
    setIsFocusMode: (val: boolean | ((prev: boolean) => boolean)) => {
      const newVal = typeof val === 'function' ? val(isFocusMode) : val;
      if (newVal) {
        closeSidebars();
      }
      setIsFocusMode(newVal);
    },
    drawerSearch,
    setDrawerSearch,
    selectedEntitySummary,
    setSelectedEntitySummary,
    isEntityDrawerOpen,
    setIsEntityDrawerOpen,
    versionToCompare,
    setVersionToCompare,
    reconstructedVersionText,
    setReconstructedVersionText,
    reconstructedPreviousText,
    setReconstructedPreviousText,
    handleToggleHistoryDrawer,
    handleToggleEntitiesDrawer,
    handleToggleSceneProperties,
    handleToggleOutlineDrawer,
    handleToggleWritersRoom,
    handleToggleFocusMode,
    closeSidebars,
  };
}
